const HEIC_FILE_PATTERN = /\.(heic|heif)$/i;
const HEIC_MIME_TYPES: Record<string, true> = {
  "image/heic": true,
  "image/heif": true,
};
const SUPPORTED_IMAGE_TYPES: Record<string, true> = {
  "image/jpeg": true,
  "image/png": true,
};
const MAX_PROFILE_PHOTO_SIZE = 500;

interface ProfilePhotoCrop {
  sourceX: number;
  sourceY: number;
  sourceSize: number;
  outputSize: number;
}

export const getProfilePhotoCrop = (
  width: number,
  height: number
): ProfilePhotoCrop => {
  const sourceSize = Math.min(width, height);

  return {
    sourceX: (width - sourceSize) / 2,
    sourceY: (height - sourceSize) / 2,
    sourceSize,
    outputSize: Math.min(sourceSize, MAX_PROFILE_PHOTO_SIZE),
  };
};

export const normalizeProfilePhoto = async (file: File): Promise<File> => {
  if (
    HEIC_MIME_TYPES[file.type.toLowerCase()] ||
    HEIC_FILE_PATTERN.test(file.name)
  ) {
    throw new Error(
      "HEIC photos aren't supported. Export the photo as a JPEG or PNG, then try again."
    );
  }

  if (!SUPPORTED_IMAGE_TYPES[file.type.toLowerCase()]) {
    throw new Error("Please choose a JPEG or PNG profile photo.");
  }

  let bitmap: ImageBitmap;
  try {
    // Browser decoding applies EXIF orientation and normalizes embedded colour
    // profiles by default before canvas encoding produces browser-safe output.
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      "We couldn't read this photo. Export it as a new JPEG or PNG, then try again."
    );
  }

  try {
    const crop = getProfilePhotoCrop(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = crop.outputSize;
    canvas.height = crop.outputSize;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Your browser couldn't prepare this profile photo.");
    }

    context.drawImage(
      bitmap,
      crop.sourceX,
      crop.sourceY,
      crop.sourceSize,
      crop.sourceSize,
      0,
      0,
      crop.outputSize,
      crop.outputSize
    );
    const outputType =
      file.type.toLowerCase() === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else {
            reject(
              new Error("Your browser couldn't prepare this profile photo.")
            );
          }
        },
        outputType,
        outputType === "image/jpeg" ? 0.9 : undefined
      );
    });
    const baseName = file.name.replace(/\.[^.]+$/, "") || "profile-photo";
    const extension = outputType === "image/png" ? "png" : "jpg";

    return new File([blob], `${baseName}.${extension}`, {
      type: outputType,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
};
